import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Tooltip,
  LinearProgress,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  CloudDownload,
  DeleteOutline,
  Refresh,
  Backup,
  Restore,
  CheckCircle,
  Error,
  Warning,
  Schedule,
  Storage,
  History,
  Description
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import { apiGet, apiPost, apiDelete } from '../../services/api';

interface BackupStats {
  totalBackups: number;
  totalSize: number;
  latestBackup: {
    name: string;
    size: number;
    created: Date;
  } | null;
  oldestBackup: {
    name: string;
    size: number;
    created: Date;
  } | null;
  backupHistory: Array<{
    name: string;
    size: number;
    created: Date;
  }>;
}

interface BackupHealth {
  healthy: boolean;
  issues: string[];
  stats: BackupStats;
}

interface Backup {
  name: string;
  size: number;
  created: Date;
}

const BackupManagement: React.FC = () => {
  const { user } = useAuth();
  const { mode } = useCustomTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [stats, setStats] = useState<BackupStats | null>(null);
  const [health, setHealth] = useState<BackupHealth | null>(null);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(true);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [restoreDialog, setRestoreDialog] = useState<{
    open: boolean;
    backupName: string;
  }>({ open: false, backupName: '' });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    backupName: string;
  }>({ open: false, backupName: '' });

  useEffect(() => {
    loadBackupData();
    const interval = setInterval(loadBackupData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadBackupData = async () => {
    setLoading(true);
    try {
      const [statsResponse, backupsResponse] = await Promise.all([
        apiGet<any>('/backups/stats'),
        apiGet<Backup[]>('/backups')
      ]);

      if (statsResponse.success) {
        setStats(statsResponse.data.stats);
        setHealth(statsResponse.data.health);
        setBackupInProgress(statsResponse.data.isBackupInProgress);
      }

      if (backupsResponse.success) {
        setBackups(backupsResponse.data);
      }

      setError(null);
    } catch (err: any) {
      setError('Failed to load backup data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    if (backupInProgress) return;

    try {
      const response = await apiPost<any>('/backups/create');
      if (response.success) {
        setSuccess('Backup started successfully');
        setBackupInProgress(true);
        setTimeout(loadBackupData, 5000);
      }
    } catch (err: any) {
      setError('Failed to start backup: ' + (err.message || 'Unknown error'));
    }
  };

  const handleRestoreBackup = async (backupName: string, dropExisting: boolean = false) => {
    try {
      const response = await apiPost<any>(`/backups/${backupName}/restore`, { dropExisting });
      if (response.success) {
        setSuccess('Backup restored successfully');
        setRestoreDialog({ open: false, backupName: '' });
        loadBackupData();
      }
    } catch (err: any) {
      setError('Failed to restore backup: ' + (err.message || 'Unknown error'));
    }
  };

  const handleDeleteBackup = async (backupName: string) => {
    try {
      const response = await apiDelete(`/backups/${backupName}`);
      if (response.success) {
        setSuccess('Backup deleted successfully');
        setDeleteDialog({ open: false, backupName: '' });
        loadBackupData();
      }
    } catch (err: any) {
      setError('Failed to delete backup: ' + (err.message || 'Unknown error'));
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getHealthColor = (healthy: boolean) => {
    return healthy ? 'success' : 'error';
  };

  const getHealthIcon = (healthy: boolean) => {
    return healthy ? <CheckCircle /> : <Error />;
  };

  return (
    <Box>
      {/* Header */}
      <Card 
        sx={{ 
          mb: 3, 
          background: mode === 'dark' 
            ? 'linear-gradient(135deg, #1a237e 0%, #311b92 100%)' 
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
        }}
      >
        <CardContent sx={{ color: 'white' }}>
          <Typography variant="h5" gutterBottom>
            Backup Management
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Monitor and manage MongoDB backups. Create, restore, and delete backups to protect your data.
          </Typography>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Loading backup data...
          </Typography>
        </Box>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Health Status */}
      {health && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip
                    icon={getHealthIcon(health.healthy)}
                    label={health.healthy ? 'System Healthy' : 'System Unhealthy'}
                    color={getHealthColor(health.healthy) as any}
                    size="medium"
                  />
                  <Typography variant="body2" color="text.secondary">
                    {backupInProgress ? 'Backup in progress...' : 'Ready'}
                  </Typography>
                </Box>
              </Grid>
              <Grid xs={12} md={6} sx={{ textAlign: 'right' }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadBackupData}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Backup />}
                  onClick={handleCreateBackup}
                  disabled={backupInProgress || loading}
                  sx={{ ml: 1 }}
                >
                  {backupInProgress ? 'Creating...' : 'Create Backup'}
                </Button>
              </Grid>
            </Grid>

            {backupInProgress && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Backup in progress...
                </Typography>
              </Box>
            )}

            {!health.healthy && health.issues.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">Issues detected:</Typography>
                <List dense>
                  {health.issues.map((issue, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <Warning fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={issue} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Statistics */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid xs={12} sm={6} md={3}>
            <Paper 
              sx={{ 
                p: 2, 
                textAlign: 'center',
                bgcolor: mode === 'dark' ? 'background.paper' : 'background.default',
                boxShadow: mode === 'dark' ? 3 : 1
              }}
            >
              <Storage sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.totalBackups}</Typography>
              <Typography variant="body2" color="text.secondary">Total Backups</Typography>
            </Paper>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Paper 
              sx={{ 
                p: 2, 
                textAlign: 'center',
                bgcolor: mode === 'dark' ? 'background.paper' : 'background.default',
                boxShadow: mode === 'dark' ? 3 : 1
              }}
            >
              <CloudDownload sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{formatSize(stats.totalSize)}</Typography>
              <Typography variant="body2" color="text.secondary">Total Size</Typography>
            </Paper>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Paper 
              sx={{ 
                p: 2, 
                textAlign: 'center',
                bgcolor: mode === 'dark' ? 'background.paper' : 'background.default',
                boxShadow: mode === 'dark' ? 3 : 1
              }}
            >
              <Schedule sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {stats.latestBackup ? Math.floor((Date.now() - new Date(stats.latestBackup.created).getTime()) / (1000 * 60 * 60)) : 0}h
              </Typography>
              <Typography variant="body2" color="text.secondary">Latest Backup Age</Typography>
            </Paper>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Paper 
              sx={{ 
                p: 2, 
                textAlign: 'center',
                bgcolor: mode === 'dark' ? 'background.paper' : 'background.default',
                boxShadow: mode === 'dark' ? 3 : 1
              }}
            >
              <History sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {stats.oldestBackup ? Math.floor((Date.now() - new Date(stats.oldestBackup.created).getTime()) / (1000 * 60 * 60 * 24)) : 0}d
              </Typography>
              <Typography variant="body2" color="text.secondary">Oldest Backup Age</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Backups Table */}
      <Card sx={{ bgcolor: mode === 'dark' ? 'background.paper' : 'background.default' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Backup History
          </Typography>
          {loading ? (
            <LinearProgress sx={{ my: 2 }} />
          ) : backups.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Storage sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No backups found. Create your first backup to get started.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Backup Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Age</TableCell>
                    {!isMobile && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {backups.map((backup) => (
                    <TableRow 
                      key={backup.name} 
                      hover
                      sx={{ 
                        '&:hover': {
                          bgcolor: mode === 'dark' ? 'action.hover' : 'action.selected'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Description fontSize="small" color="primary" />
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {backup.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatSize(backup.size)}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(backup.created)}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${Math.floor((Date.now() - new Date(backup.created).getTime()) / (1000 * 60 * 60))}h ago`}
                          size="small"
                          variant="outlined"
                          color={mode === 'dark' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      {!isMobile && (
                        <TableCell align="right">
                          <Tooltip title="Restore">
                            <IconButton
                              size="small"
                              onClick={() => setRestoreDialog({ open: true, backupName: backup.name })}
                              sx={{ color: mode === 'dark' ? 'primary.main' : 'primary' }}
                            >
                              <Restore />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteDialog({ open: true, backupName: backup.name })}
                            >
                              <DeleteOutline />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Restore Dialog */}
      <Dialog open={restoreDialog.open} onClose={() => setRestoreDialog({ open: false, backupName: '' })}>
        <DialogTitle>Restore Backup</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to restore backup "{restoreDialog.backupName}"? This will replace the current database data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false, backupName: '' })}>Cancel</Button>
          <Button onClick={() => handleRestoreBackup(restoreDialog.backupName, true)} color="primary">
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, backupName: '' })}>
        <DialogTitle>Delete Backup</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete backup "{deleteDialog.backupName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, backupName: '' })}>Cancel</Button>
          <Button onClick={() => handleDeleteBackup(deleteDialog.backupName)} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BackupManagement;
